import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { useRef, useState } from "react";
import type { SkillsListSearchProps } from "../types";

export const SkillsListSearch = ({
  search,
  onSearchChange,
}: SkillsListSearchProps) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [isSearchTooltipOpen, setIsSearchTooltipOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const closeSearchTooltip = () => {
    setIsSearchTooltipOpen(false);
  };

  const openSearchTooltip = () => {
    if (!searchOpen) {
      setIsSearchTooltipOpen(true);
    }
  };

  const openSearch = () => {
    closeSearchTooltip();
    setSearchOpen(true);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const closeSearch = () => {
    setSearchOpen(false);
    onSearchChange("");
  };

  return (
    <Box
      data-testid="skills-list-search-wrapper"
      sx={{
        display: "flex",
        alignItems: "center",
      }}
      style={{
        flex: searchOpen ? "1 1 auto" : "0 0 auto",
        marginLeft: searchOpen ? "2px" : 0,
        minWidth: 0,
      }}
    >
      <Collapse in={!searchOpen} orientation="horizontal" timeout={220}>
        <Tooltip
          title="Search skills"
          open={isSearchTooltipOpen && !searchOpen}
          onClose={closeSearchTooltip}
          onOpen={openSearchTooltip}
          TransitionProps={{ timeout: 0 }}
        >
          <IconButton
            size="small"
            aria-label="Search skills"
            aria-hidden={searchOpen}
            data-testid="skills-list-search-trigger-button"
            tabIndex={searchOpen ? -1 : undefined}
            onMouseDown={closeSearchTooltip}
            onClick={openSearch}
            sx={{
              backgroundColor: search
                ? "rgba(7, 78, 232, 0.08)"
                : "transparent",
              color: search ? "primary.main" : "text.secondary",
              pointerEvents: searchOpen ? "none" : undefined,
              visibility: searchOpen ? "hidden" : "visible",
            }}
          >
            <SearchIcon
              data-testid="skills-list-search-trigger-icon"
              sx={{ fontSize: 16 }}
            />
          </IconButton>
        </Tooltip>
      </Collapse>
      <Collapse
        data-testid="skills-list-search-field-collapse"
        in={searchOpen}
        orientation="horizontal"
        timeout={220}
        sx={{
          "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
            width: "100%",
          },
        }}
        style={{
          flex: searchOpen ? "1 1 auto" : "0 0 auto",
          minWidth: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          hiddenLabel
          variant="standard"
          inputRef={searchInputRef}
          placeholder="Search"
          style={{ width: "100%" }}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              closeSearch();
            }
          }}
          inputProps={{ "aria-label": "Search skills" }}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: 14,
              "& input": {
                py: 0,
                height: 28,
                lineHeight: "28px",
              },
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  aria-hidden={!searchOpen}
                  data-testid="skills-list-search-field-icon"
                  sx={{
                    color: "rgba(100, 116, 139, 0.9)",
                    fontSize: 16,
                    visibility: searchOpen ? "visible" : "hidden",
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Close search"
                  onClick={closeSearch}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Collapse>
    </Box>
  );
};
